import Alerts from 'lib/alerts.js';
import Log from 'lib/log';
import BaseRouter from './Base.js';
import LayoutView from '../views/payroll/Layout.js';
import HistoryView from '../views/PayrollHistory.js';
import PayrollView from '../views/payroll/PayrollView.js';
import PayrollDetails from 'newCollections/payroll-details.js';
import Payslips from 'newCollections/payslips.js';
import PayslipDetails from 'newCollections/payslip-details.js';
import Attachments from 'newCollections/attachments.js';
import PayrollSummary from 'newCollections/payroll-summary.js';
import Payroll from 'newModels/payroll.js';
import moment from 'moment';
const log = Log('employer/payroll-router');

module.exports = BaseRouter.extend({
    routes: {
        'payroll':               'payroll',
        'payroll/step/:step':    'payrollStep',
        'payroll/history':       'history',
        'payroll/view/:id/:tab': 'payrollView'
    },

    titles: {
        'payroll':               'Run Payroll',
        'payroll/step/:step':    'Run Payroll',
        'payroll/history':       'Payroll History',
        'payroll/view/:id/:tab': 'Payroll'
    },

    initialize: function(options) {
        this.options = _.defaults(options || {}, {log: log});
        BaseRouter.prototype.initialize.apply(this, [this.options]);
    },

    payroll: function() {
        if (app.user.get('isMPPCustomer')) {
            return;
        }

        app.contentView = this.layout = new LayoutView({el: this.el});
        this.layout.showLoader();

        return Promise.all([
            app.company.fetchCurrentPayroll(),
            app.company.destroyUnsavedPayrolls()
        ]).then(([payroll]) => {
            if (payroll) {
                return Promise.all([
                    Promise.resolve(payroll),
                    payroll.hasPendingJobs()
                ]);
            }
            return [false, false];
        })
            .then(([payroll, hasPendingJobs]) => {
                if (!payroll || payroll.get('status') < 1) {
                    this.navigate(
                        'payroll/step/1',
                        {trigger: true, replace: true}
                    );
                    return;
                }

                this._payroll = payroll;
                this._pendingJobs = hasPendingJobs;
                this.navigate(
                    'payroll/step/' + (payroll.get('status') + 1),
                    {trigger: true, replace: true}
                );
            });
    },

    loadPayrollForStep: function(step) {
        let promise;

        if (this._payroll && step > 1) {
            promise = Promise.all([
                Promise.resolve(this._payroll),
                Promise.resolve(this._pendingJobs)
            ]);
            this._payroll = null;
            this._pendingJobs = null;
        } else {
            promise = Promise.all([
                app.company.fetchCurrentPayroll(),
                app.company.fetchUnsavedPayroll()
            ]).then(([payroll, unsavedPayroll]) => {
                if (!payroll && unsavedPayroll && step === 2) {
                    payroll = unsavedPayroll;
                }
                if (payroll) {
                    return Promise.all([
                        Promise.resolve(payroll),
                        payroll.hasPendingJobs()
                    ]);
                }

                if (!payroll && step === 1) {
                    payroll = new Payroll({
                        companyId: app.company.id,
                        periodDate: Payroll.getCurrentPeriodDate()
                    });
                }
                return Promise.all([
                    Promise.resolve(payroll),
                    Promise.resolve(false)
                ]);
            });
        }
        return promise;
    },

    // TODO I got rid of the crappy
    // step[One|Two|Three] but some of the crapiness in them I placed here
    // to be reviewed later.
    loadStepViewData: function(payroll, pendingJobs, step) {
        const loaders = [
            (payroll, pendingJobs) => { //step 1
                const date = payroll.getPeriod().toDate();
                if (payroll.isNew()) {
                    payroll.set('periodDate', date);
                }
                return Promise.all([
                    app.company.fetchLastSubmittedPayroll(),
                    app.company.fetchPayableEmployees(null, 10, date),
                    app.company.fetchPayableEmployees(null, null, date),
                    app.company.fetch()
                ]).then(([
                    lastPayroll, employees, allEmployees
                ]) => {
                    if (lastPayroll && !lastPayroll.isComplete()) {
                        throw new Error('Unfinished Payroll Present');
                    }

                    if (employees.length === 0) {
                        throw new Error('No Payable Employee');
                    }

                    const payrollItems =
                        app.payrollItems.getSelectableAdditionals();

                    const details = new PayrollDetails(null, {
                        payroll,
                        filter: {limit: 9999},
                        pagination: false
                    });
                    const lastPayrollDetails = new PayrollDetails(null, {
                        payroll: lastPayroll,
                        filter: {
                            where: {amount: {gt: 0}},
                            limit: 9999
                        },
                        pagination: false
                    });

                    return Promise.all([
                        payroll.isNew() ?
                            PayrollDetails.createDefaults(
                                payroll, employees.slice(0, 10)
                            ) :
                            details.fetch().then(() => {
                                return details.filterNonContributions();
                            }),
                        lastPayroll ? lastPayrollDetails.fetch()
                            .then(() => {
                                return lastPayrollDetails
                                    .filterNonContributions();
                            }) : Promise.resolve(lastPayrollDetails)
                    ]).then(([details, lastDetails]) => {
                        return Promise.resolve({
                            model: payroll,
                            pendingJobs,
                            payrollDetails: details,
                            lastPayroll: lastPayroll,
                            lastPayrollDetails: lastDetails,
                            employees,
                            allEmployees,
                            payrollItems
                        });
                    });
                });
            },

            (payroll) => { //step 2
                // There is a combobox widget in step 2 that requires all
                // payslips. Until it can be ajaxified, this will have to do
                return payroll.fetchPayslips()
                    .then(payslips => {
                        if (!payslips.length) {
                            return Alerts
                                .show('warning', {
                                    text: `The payroll you created has no \
                                        payslips. <a href="https://help.\
                                        payrollpanda.my/18570-app-faq170577-\
                                        how-can-i-send-the-payslips-to-my-\
                                        employees" target="_blank">Learn more\
                                        </a> if you want to send payslips.`,
                                    confirmButtonText: 'Back To Step 1'
                                })
                                .then(() => payroll.setStatus(0))
                                .then(() => app.mainRouter.navigate(
                                    'payroll/step/1', {trigger: true}
                                ));
                        }

                        payroll.setCompany(app.company);
                        return Promise.all(
                            payslips.map(payslip => {
                                payslip.setPayroll(payroll);
                                payslip.setCompany(app.company);
                                return payslip.fetchEmployee()
                                    .then(emp => emp.fetchPosition());
                            })
                        ).then(() => payslips);
                    }).then(payslips => {
                        const attachments = new Attachments(null, {
                            attachable: payroll,
                            pagination: false
                        });
                        return Promise.all([
                            payslips,
                            app.company.fetchPrimaryLocation()
                                .then(location => location.fetchAddress()
                                    .then(() => location)
                                ),
                            attachments.fetch()
                        ]);
                    }).then(([payslips, location, attachments]) =>
                        Promise.resolve({
                            model: payroll,
                            payslips: payslips,
                            payslipDetails: new PayslipDetails(null, {}),
                            payrollSummary: new PayrollSummary(null, {
                                payroll,
                                filter: {
                                    limit: 10
                                },
                                pagination: {rowsPerPage: 10}
                            }),
                            companyLocation: location,
                            attachments,
                            tab: 'summary'
                        }));
            },

            (payroll) => { //step 3
                const payslips = new Payslips(null, {
                    payroll,
                    pagination: false,
                    sortKey: 'employeeName',
                    sortOrder: 'ascending'
                });
                payroll.setCompany(app.company);
                return Promise.all([
                    app.company.fetchCurrentPayroll(),
                    app.company.fetchBankAccount(),
                    payslips.fetch({
                        filter: {limit: 9999}
                    })
                ]).then(() => {
                    const isFree = !app.company.get('isBillable');
                    if (isFree) {
                        return 0;
                    }

                    return payroll.calculateBillableAmount();
                }).then(amount => {
                    payroll.set('billableAmount', amount);
                    return Promise.all(
                        payslips.map(payslip => {
                            return payslip.fetchEmployee()
                                .then(employee => employee.fetchPosition());
                        })
                    );
                }).then(() => {
                    return {
                        payslips: payslips,
                        model: payroll
                    };
                });
            }
        ];

        return loaders[(step - 1)](payroll, pendingJobs);
    },

    payrollStep: function(step) {
        // 20200827 disable payroll runs between 2-5am,
        // during which fix-YTD is running
        if (
            moment().hour() >= 2 &&
            moment().hour() < 5
        ) {
            return Alerts.show('warning', {
                text: `Please note maintenance is in progress. \
                    Please check back after 5 am to run payroll.`,
                confirmButtonText: 'OK',
                callback: () =>
                    app.mainRouter.navigate(
                        'dashboard',
                        {trigger: true}
                    )
            });
        }

        if (app.user.get('isMPPCustomer')) {
            return;
        }

        app.contentView = this.layout = new LayoutView({el: this.el});

        step = parseInt(step, 10);
        if (step < 1 || step > 3) {
            step = 1;
        }

        this.layout.step = step;
        this.layout.render();

        this.loadPayrollForStep(step)
            .then(([payroll, pendingJobs]) => {
                if (!payroll || payroll.get('status') !== (step - 1)) {
                    return app.mainRouter.navigate(
                        'payroll',
                        {trigger: true, replace: true}
                    );
                }

                this.layout.model = payroll;
                this.layout.render();

                return this.loadStepViewData(payroll, pendingJobs, step)
                    .then(viewData => {
                        this.layout.renderContent(viewData);
                    });
            })
            .catch(err => {
                if (err.message === 'Unfinished Payroll Present') {
                    return Alerts.show('warning', {
                        text: `You have an unfinished payroll. Please submit \
                            it before starting the next payroll.`,
                        confirmButtonText: 'View Payroll History',
                        callback: () =>
                            app.mainRouter.navigate(
                                'payroll/history', {trigger: true}
                            )
                    });
                } else if (err.message === 'No Payable Employee') {
                    return Alerts.show('warning', {
                        text: 'You need to add at least one employee before ' +
                            'you can Run Payroll.',
                        confirmButtonText:  'Add Employee',
                        callback: () =>
                            app.mainRouter.navigate(
                                'employee/add', {trigger: true}
                            )
                    });
                }
                log.error(err);
                Alerts.show('criticalError', err);
            });
    },

    history: function() {
        let attachments = {};
        let integration = null;
        const payrollIntegrations = {};
        let payrolls;

        this.view = new HistoryView({el: this.el});

        return app.company.fetchPayrolls()
            .then(() => payrolls = app.company.payrolls)
            .then(() => app.company.fetchIntegration())
            .then(() => integration = app.company.getIntegration())
            .then(() => {
                const payrollAttachments = payrolls.map(p => {
                    const atmts = new Attachments(null, {
                        attachable: p,
                        pagination: false
                    });

                    return atmts.fetch({filter: {limit: 10000}})
                        .then(() => {
                            return [
                                p.id,
                                atmts
                            ];
                        });
                });

                return Promise.all(payrollAttachments);
            })
            .then((payrollAttachments) => {
                attachments = _.object(payrollAttachments);

                this.view.setCollection({
                    collection: payrolls,
                    attachments,
                    integration,
                    payrollIntegrations
                });
            })
            .catch(err => { Alerts.show('criticalError', err); });
    },

    payrollView: function(id, tab) {
        app.contentView = this.layout = new PayrollView({el: this.el});

        return new Payroll({objectId: id})
            .fetch()
            .then(payroll => {
                if (!payroll) {
                    throw new Error('Payroll not found');
                }

                payroll.setCompany(app.company);

                this.layout.period = payroll.getPeriodFormatted();
                this.layout.render();

                // make sure payslips include employee data
                const fetchPayslips = () => {
                    return payroll.fetchPayslips()
                        .then(payslips => Promise.all(
                            payslips.map(payslip => {
                                payslip.setPayroll(payroll);
                                payslip.setCompany(app.company);
                                return payslip.fetchEmployee();
                            })
                        ).then(employees => Promise.all(
                            employees.map(employee => {
                                return employee.fetchPosition();
                            }))
                        ).then(() => payslips));
                };

                // make sure address is included
                const fetchLocation = () => {
                    return app.company.fetchPrimaryLocation()
                        .then(location => {
                            return location.fetchAddress()
                                .then(() => location);
                        });
                };

                return Promise.all([
                    fetchPayslips(),
                    fetchLocation()
                ])
                    .then(([payslips, location]) => {
                        this.layout.renderContent({
                            model: payroll,
                            payslips,
                            payslipDetails: new PayslipDetails(null, {}),
                            payrollSummary: new PayrollSummary(null, {
                                filter: {
                                    where: {payrollId: id},
                                    limit: 10
                                },
                                pagination: {rowsPerPage: 10},
                                payroll: payroll
                            }),
                            companyLocation: location,
                            tab: tab
                        });
                    })
                    .catch(err => { Alerts.show('criticalError', err); });
            });
    }
});



// WEBPACK FOOTER //
// ./app/employer/routers/Payroll.js